import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEye, FiUpload, FiX } from 'react-icons/fi';

import { show } from 'store/modules/dialog/actions';
import api from 'services/api';

import Card from 'components/Card';
import Button from 'components/Button';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import Spinner from 'components/Spinner';
import TextArea from 'components/TextArea';
import DatePicker from 'components/DatePicker';

import {
  Container,
  Content,
  Form,
  FormContent,
  ColumnContent,
  FormActions,
  Image,
  DropContainer,
  ThumbnailContainer,
  ButtonUpload,
  ButtonUploadError,
  CircularContainer,
  Upload,
  FileContent,
} from './styles';
import { FormHandles } from '@unform/core';
import { AnuncioJornal, Jornal } from 'pages/@types';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { Permissao, StatusAnuncioJornal } from 'utils/variables';
import Dropzone from 'react-dropzone';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';

import jornal from 'assets/jornal.png';
import nojornal from 'assets/nojornal.png';

import { usePermit } from 'utils/hooks/usePermit';
import { useModal } from 'hooks/modal';
import { addHours } from 'date-fns';
import Radio from 'components/Radio';
interface IJornaisOption {
  id: number;
  label: string;
}
// configuração do validator
const schema = Yup.object().shape({
  titulo: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[a-zA-Z0-9- ./]*$/, 'Titulo contém caracteres especiais')
    .max(26, 'Titulo excedeu o limite de 26 caracteres'),
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início inválida'),
  jornais: Yup.string().required('Campo Obrigatório'),
  formato: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
});

export default function AnuncioJornalStore() {
  // react-router-dom
  const navigate = useNavigate();
  // redux
  const dispatch = useDispatch();
  const hasAvaliador = usePermit([], [Permissao.AVALIADOR]);
  /**
   * USE STATE
   */
  const [jornais, setJornais] = useState<IJornaisOption[]>([]);
  const [todos, setTodos] = useState(false);
  const [status, setStatus] = useState('');
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingJornais, setLoadingJornais] = useState(true);
  const [initialData, setInitialData] = useState({} as AnuncioJornal);
  const [anuncioFile, setAnuncioFile] = useState<any[]>([]);
  const { showModal, hideModal } = useModal();
  /**
   * USE PARAMS
   */
  const { anunciojornalId } = useParams<{ anunciojornalId: string }>();

  /**
   * USE REF
   */
  const formRef = useRef<FormHandles>(null);
  const formRefObsReprova = useRef<FormHandles>(null);
  const obsReprova = useRef('');

  /**
   * USE CALLBACK
   */

  const updateFile = useCallback((file, data) => {
    setAnuncioFile([{ ...file, ...data }]);
  }, []);

  const handleSelectedJornal = useCallback(() => {
    if (
      formRef.current &&
      formRef.current.getFieldValue('jornais').length === jornais.length
    )
      setTodos(true);
  }, [jornais.length]);

  const handleTodos = useCallback(
    e => {
      e.preventDefault();

      if (formRef.current) {
        if (
          formRef.current.getFieldValue('jornais').length === jornais.length
        ) {
          formRef.current.clearField('jornais');
          setTodos(false);
        } else {
          formRef.current.setFieldValue(
            'jornais',
            jornais.map(j => j.id).join(),
          );
          setTodos(true);
        }
      }
    },
    [jornais],
  );

  /**
   * FUNÇÕES
   */

  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        setLoadingSubmit(true);
        if (formRef.current) formRef.current.setErrors({});
        let response;
        // criando novo anuncio
        await schema.validate(data, { abortEarly: false });
        delete data.file;
        if (!data.observacao) delete data.observacao;
        if (!anunciojornalId) response = await api.post('anunciojornal', data);
        else
          response = await api.put(`anunciojornal/${anunciojornalId}`, {
            ...data,
            ...(obsReprova.current && { obsreprova: obsReprova.current }),
            status,
          });

        if (response && response.data)
          handleSuccessMessage({
            contentHTML: `<h3>Anúncio de Jornal salvo!</h3> `,
            redirectTo: anunciojornalId
              ? `/anunciojornal`
              : `/anunciojornal/${response.data.id}`,
          });
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [anunciojornalId, handleSuccessMessage, obsReprova, status],
  );

  const handleDragReject = useCallback(
    files => {
      if (files) {
        const ext = files[0].name.split('.')[1];
        const text = `Não é permitido o envio de arquivo com extensão .${ext}`;

        dispatch(show({ type: 'error', text }));
      }
    },
    [dispatch],
  );

  const processUpload = useCallback(
    async selectedFile => {
      if (selectedFile) {
        const data = new FormData();
        data.append('file', selectedFile.file);
        try {
          const response = await api.patch(
            `/anunciojornalfile/${anunciojornalId}`,
            data,
            {
              timeout: 180000 * 3,
              onUploadProgress: e => {
                const progress = e.total
                  ? Math.round((e.loaded * 100) / e.total)
                  : 0;
                updateFile(selectedFile, {
                  progress,
                });
              },
            },
          );
          updateFile(selectedFile, {
            uploaded: true,
            progress: 100,
            error: false,
          });
          setTimeout(() => setAnuncioFile([]), 1000);
          setInitialData({
            ...initialData,
            filename: selectedFile.file.path,
            download_url: response.data.download_url,
          });
          toast.success('Arquivo carregado com sucesso');
        } catch (err) {
          updateFile(selectedFile.id, {
            error: true,
            progress: 0,
            uploaded: false,
          });
          setTimeout(() => setAnuncioFile([]), 1000);
          if (err instanceof Error) {
            toast.error(err.message);
          } else {
            const appError = err as AppError;
            console.log(appError);
            toast.error(
              'Falha ao carregar o arquivo. Tente novamente mais tarde',
            );
          }
        }
      }
    },
    [anunciojornalId, initialData, updateFile],
  );

  const handleUpload = useCallback(
    files => {
      const filesFormatted = files.map((file: any) => ({
        file,
        id: uniqueId(),
        name: file.name,
        readableSize: filesize(file.size),
        progress: 0,
        uploaded: false,
        preview: URL.createObjectURL(file),
        error: false,
      }));

      filesFormatted.map((file: any) => processUpload(file));
    },
    [processUpload],
  );

  const handleSubmitObsAvaliacao = useCallback(
    async data => {
      if (formRefObsReprova.current) {
        try {
          formRefObsReprova.current.setErrors({});

          const schema = Yup.object().shape({
            obsreprova: Yup.string()
              .required('Preencha a observação')
              .max(800, 'A observação excede o limite de 800 caracteres'),
          });

          await schema.validate(data, { abortEarly: false });

          obsReprova.current = data.obsreprova;
          if (formRef.current) formRef.current.submitForm();

          hideModal();
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formRefObsReprova.current.setErrors(errors);
            return;
          }
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      }
    },
    [hideModal],
  );

  const renderObsAvaliacao = useCallback(() => {
    return (
      <Form ref={formRefObsReprova} onSubmit={handleSubmitObsAvaliacao}>
        <TextArea
          placeholder="Observação"
          name="obsreprova"
          height="calc(100vh - 667px)"
          disable={false}
          errorField=""
          icon=""
        />
        <div className="actions">
          <Button color="default" onClick={() => hideModal()}>
            CANCELAR
          </Button>
          <Button type="submit" color="primary">
            CONFIRMAR
          </Button>
        </div>
      </Form>
    );
  }, [handleSubmitObsAvaliacao, hideModal]);

  const handleAvaliacao = useCallback(
    status => {
      setStatus(status);
      showModal({
        title: 'OBSERVAÇÕES',
        component: renderObsAvaliacao(),
        width: '30vw',
      });
    },
    [renderObsAvaliacao, showModal],
  );

  function renderDragButton(
    isDragActive: boolean,
    isDragReject: boolean,
    hasFile = false,
  ) {
    if (!isDragActive) {
      return (
        <>
          <ButtonUpload hasMidia={hasFile} onClick={e => e.preventDefault()}>
            <FiUpload />
            <p> {hasFile ? 'Substituir arquivo' : 'Arraste ou clique'}</p>
          </ButtonUpload>
        </>
      );
    }
    if (isDragReject) {
      return (
        <ButtonUploadError hasMidia={hasFile}>
          <FiX /> <p>Formato não permitido</p>
        </ButtonUploadError>
      );
    }
    return (
      <ButtonUpload hasMidia={hasFile}>
        <FiUpload /> <p>Solte o arquivo</p>
      </ButtonUpload>
    );
  }

  useEffect(() => {
    async function loadAnuncio() {
      setLoading(true);
      try {
        const response = await api.get(`/anunciojornal/${anunciojornalId}`);
        if (response && response.data) {
          setStatus(response.data.status);
          setInitialData({
            ...response.data,
            datainicio: response.data.datainicio
              ? addHours(new Date(response.data.datainicio), 3)
              : null,
            datafim: response.data.datafim
              ? addHours(new Date(response.data.datafim), 3)
              : null,
            observacao: response.data.observacao,
            jornais: response.data.jornal.map((j: Jornal) => j.id),
            formato: response.data.formato,
          });
        } else toast.error('Falha de comunicação com o servidor');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    // Verifica se é edição
    if (anunciojornalId) {
      loadAnuncio();
    }
  }, [anunciojornalId]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadJornais() {
      setLoadingJornais(true);
      try {
        const response = await api.get('/jornais');
        if (response && response.data)
          setJornais(
            response.data.map((jornal: Jornal) => {
              return { id: jornal.id, label: jornal.nome };
            }),
          );
        else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingJornais(false);
    }
    loadJornais();
    return () => {
      controller.abort();
    };
  }, []);

  /**
   * USE MEMO
   */

  const inUpload = useMemo(
    () =>
      !!anuncioFile &&
      anuncioFile.length > 0 &&
      !anuncioFile[0].uploaded &&
      !anuncioFile[0].error &&
      anuncioFile[0].progress > 0,

    [anuncioFile],
  );

  return (
    <Container>
      <header>
        <Button
          color="primary"
          onClick={e => {
            e.preventDefault();
            navigate(-1);
          }}
        >
          VOLTAR
          <FiArrowLeft />
        </Button>
      </header>
      <Card>
        <header>
          <h4>CRIAR NOVO ANÚNCIO</h4>
        </header>
        <Content>
          {!loading && !loadingJornais && (
            <Form
              ref={formRef}
              initialData={initialData}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div>
                <FormContent>
                  <ColumnContent $columns="1fr 1fr">
                    <div>
                      <section>
                        <h5>Dados gerais: </h5>
                      </section>
                      <div>
                        <Input
                          name="titulo"
                          placeholder="Título *"
                          disabled={
                            loadingSubmit ||
                            initialData.status === StatusAnuncioJornal.ENVIADO
                          }
                        />
                      </div>
                      <div>
                        <section>
                          <h5>PERÍODO DE VEICULAÇÃO: *</h5>
                        </section>
                        <ColumnContent $columns="1fr 1fr 4fr">
                          <DatePicker
                            placeholderText="DE"
                            minDate={new Date()}
                            name="datainicio"
                            disabled={
                              loading ||
                              initialData.status === StatusAnuncioJornal.ENVIADO
                            }
                          />

                          <DatePicker
                            placeholderText="ATÉ"
                            minDate={new Date()}
                            name="datafim"
                            disabled={
                              loading ||
                              initialData.status === StatusAnuncioJornal.ENVIADO
                            }
                          />
                        </ColumnContent>
                        <div>
                          <section>
                            <h5>Jornais: *</h5>
                            <Button
                              fill
                              color="primary"
                              size="xsm"
                              onClick={handleTodos}
                            >
                              TODOS ? {todos ? 'SIM' : 'NÃO'}
                            </Button>
                          </section>

                          <Checkbox
                            name="jornais"
                            options={jornais}
                            disabled={
                              loadingSubmit ||
                              initialData.status === StatusAnuncioJornal.ENVIADO
                            }
                            handleSelected={handleSelectedJornal}
                          />
                        </div>
                        <div>
                          <section>
                            <h5>Qual o formato do Anúncio? *</h5>
                          </section>
                          <Radio
                            name="formato"
                            options={[
                              { id: 'meia-página', label: 'MEIA-PÁGINA' },
                              { id: 'página inteira', label: 'PÁGINA INTEIRA' },
                              {
                                id: 'banners digital',
                                label: 'BANNERS DIGITAL',
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                    {anunciojornalId && (
                      <FileContent>
                        <Upload>
                          <Dropzone
                            accept={{
                              'application/pdf': ['.pdf'],
                            }}
                            multiple={false}
                            onDropRejected={handleDragReject}
                            onDropAccepted={handleUpload}
                            disabled={[
                              StatusAnuncioJornal.ENVIADO,
                              StatusAnuncioJornal.APROVACAO,
                            ].includes(initialData.status)}
                          >
                            {({
                              getRootProps,
                              getInputProps,
                              isDragActive,
                              isDragReject,
                            }) => (
                              <DropContainer
                                {...getRootProps()}
                                isDragActive={isDragActive}
                                isDragReject={isDragReject}
                              >
                                {!inUpload && <input {...getInputProps()} />}
                                <ThumbnailContainer
                                  hasMidia={!!initialData.filename}
                                  inUpload={!!inUpload}
                                >
                                  <Image
                                    src=""
                                    loader={<Spinner />}
                                    unloader={
                                      <img
                                        src={
                                          initialData.filename
                                            ? jornal
                                            : nojornal
                                        }
                                        alt="sem thumb"
                                      />
                                    }
                                  />
                                  {!inUpload &&
                                    ![
                                      StatusAnuncioJornal.ENVIADO,
                                      StatusAnuncioJornal.APROVACAO,
                                    ].includes(initialData.status) &&
                                    renderDragButton(
                                      isDragActive,
                                      isDragReject,
                                      !!initialData.filename,
                                    )}
                                </ThumbnailContainer>
                              </DropContainer>
                            )}
                          </Dropzone>

                          {!!anuncioFile && anuncioFile.length > 0 && (
                            <CircularContainer
                              styles={{
                                root: { width: 124 },
                                path: { stroke: '#3358f4' },
                                text: {
                                  fill: '#3358f4',
                                  fontSize: '22px',
                                  fontWeight: 600,
                                },
                              }}
                              text={`${anuncioFile[0].progress}%`}
                              strokeWidth={5}
                              value={anuncioFile[0].progress}
                            />
                          )}
                        </Upload>
                        <div>
                          {initialData.filename && (
                            <Button
                              id={`id_anuncio_${initialData.id}`}
                              fill
                              color="font"
                              round
                              onClick={e => {
                                e.preventDefault();
                                window.open(
                                  `${initialData.download_url}`,
                                  '_blank',
                                );
                              }}
                            >
                              <FiEye />
                            </Button>
                          )}
                        </div>
                      </FileContent>
                    )}
                  </ColumnContent>
                  <ColumnContent $columns="1fr">
                    <section>
                      <h5>Observação:</h5>
                    </section>
                    <TextArea
                      name="observacao"
                      height="100"
                      placeholder="Mensagem que será exibida no email"
                      icon=""
                      errorField=""
                      disabled={
                        loadingSubmit ||
                        initialData.status === StatusAnuncioJornal.ENVIADO
                      }
                    />
                  </ColumnContent>
                </FormContent>
              </div>
              <FormActions>
                {([
                  StatusAnuncioJornal.EMPROCESSO,
                  StatusAnuncioJornal.ALTERACAO,
                ].includes(initialData.status) ||
                  !initialData.status) && (
                  <>
                    <Button
                      type="submit"
                      disabled={loadingSubmit}
                      color="primary"
                    >
                      {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                    </Button>
                    {anunciojornalId && (
                      <Button
                        color="primary"
                        onClick={() => setStatus(StatusAnuncioJornal.APROVACAO)}
                        disabled={!initialData.filename || loadingSubmit}
                      >
                        {!loadingSubmit ? (
                          'ENVIAR PARA A APROVAÇÃO'
                        ) : (
                          <Spinner type="ThreeDots" />
                        )}
                      </Button>
                    )}
                  </>
                )}
                {initialData.status === StatusAnuncioJornal.APROVACAO &&
                  (hasAvaliador ? (
                    <>
                      <Button
                        color="success"
                        onClick={() => setStatus(StatusAnuncioJornal.ENVIADO)}
                        disabled={
                          initialData.status === StatusAnuncioJornal.ENVIADO ||
                          loadingSubmit
                        }
                      >
                        {!loadingSubmit ? (
                          'APROVAR'
                        ) : (
                          <Spinner type="ThreeDots" />
                        )}
                      </Button>
                      <Button
                        color="danger"
                        onClick={e => {
                          e.preventDefault();
                          handleAvaliacao(StatusAnuncioJornal.ALTERACAO);
                        }}
                        disabled={
                          initialData.status === StatusAnuncioJornal.ENVIADO ||
                          loadingSubmit
                        }
                      >
                        {!loadingSubmit ? (
                          'REPROVAR'
                        ) : (
                          <Spinner type="ThreeDots" />
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      color="danger"
                      type="submit"
                      onClick={() => setStatus(StatusAnuncioJornal.ALTERACAO)}
                    >
                      {!loadingSubmit ? (
                        'VOLTAR PARA ALTERAÇÃO'
                      ) : (
                        <Spinner type="ThreeDots" />
                      )}
                    </Button>
                  ))}
              </FormActions>
            </Form>
          )}
        </Content>
      </Card>
    </Container>
  );
}
